import React, { memo, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ExcessUsersBanner as Web5ExcessUsersBanner } from '@Sage/ssm-web5';

import { ConfigActions } from 'actions';
import Context from 'managers/Context';
import { ADMIN_URL } from 'constants/Environment';
import { getConfig } from 'services/Onboarding';

const ExcessUsersBanner = () => {
    const dispatch = useDispatch();
    const lastExcessTotalsRef = useRef(null);

    const {
        canConfigImplementation,
        manageUsersFromWeb,
        productModeCode,
        subscriptionModeCode,
        totalPurchased,
        totalUser,
    } = useSelector((state) => ({
        canConfigImplementation: state.config?.permission?.canConfigImplementation,
        manageUsersFromWeb: state.config?.userData?.manageUsersFromWeb,
        productModeCode: state.config?.productModeCode,
        subscriptionModeCode: state.config?.subscriptionModeCode,
        totalPurchased: state.config?.totalPurchased,
        totalUser: state.config?.totalUser,
    }));

    const sessionData = Context.cacheManager?.getConfigStore?.() ?? {};
    const totalPurchasedRaw = totalPurchased ?? sessionData.totalPurchased;
    const totalUserRaw = totalUser ?? sessionData.totalUser;

    const isFreeImpl = !!subscriptionModeCode?.includes('free');
    const purchased = Number(totalPurchasedRaw);
    const billable = Number(totalUserRaw);

    const countsLoaded = Number.isFinite(purchased) && Number.isFinite(billable);

    useEffect(() => {
        if (!countsLoaded || isFreeImpl) return;
        if (purchased > 0 && billable > purchased) {
            lastExcessTotalsRef.current = { purchased, billable };
        } else if (purchased > 0) {
            lastExcessTotalsRef.current = null;
        }
    }, [billable, countsLoaded, isFreeImpl, purchased]);

    const fallback = lastExcessTotalsRef.current;
    const useStaleTotals = !countsLoaded && fallback;
    const purchasedResolved = useStaleTotals ? fallback.purchased : purchased;
    const billableResolved = useStaleTotals ? fallback.billable : billable;

    const enabled =
        !isFreeImpl &&
        Number.isFinite(purchasedResolved) &&
        purchasedResolved > 0 &&
        Number.isFinite(billableResolved) &&
        billableResolved > purchasedResolved;

    const href = canConfigImplementation
        ? manageUsersFromWeb
            ? '#/settings/users'
            : `${ADMIN_URL}?token=${Context?.config?.token}&entityToGo=users`
        : null;

    useEffect(() => {
        if (!productModeCode) return;

        const cached = Context.cacheManager?.getConfigStore?.() ?? {};
        const hasPurchased = Number.isFinite(Number(totalPurchased ?? cached.totalPurchased));
        const hasBillable = Number.isFinite(Number(totalUser ?? cached.totalUser));

        if (hasPurchased && hasBillable) return;

        getConfig(productModeCode)
            .then((config = {}) => {
                dispatch(
                    ConfigActions.updateConfig({
                        totalUser: config.totalUser,
                        totalPurchased: config.totalPurchased,
                    }),
                );
            })
            .catch((error) => {
                console.error(error);
            });
    }, [dispatch, productModeCode, totalPurchased, totalUser]);

    if (!enabled) return null;

    return (
        <Web5ExcessUsersBanner
            enabled={enabled}
            totalBillableUsers={billableResolved}
            totalUsersPurchased={purchasedResolved}
            href={href}
            linkTarget="_blank"
            linkRel="noreferrer"
        />
    );
};

export default memo(ExcessUsersBanner);
